import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchStocktakes } from '@/services/stocktake';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useAuth } from '@/context/AuthContext';
import { CreateStocktakeModal } from '@/features/stocktake/CreateStocktakeModal';
import { StocktakeCountModal } from '@/features/stocktake/StocktakeCountModal';
import { Stocktake } from '@/types/models';
import {
  ClipboardList,
  ClipboardCheck,
  Loader2,
  Plus,
  Search,
  ShieldAlert,
} from 'lucide-react';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' });
}

export const StocktakePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const isManagerOrOwner = hasPermission('purchases');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['stocktakes', search, statusFilter, page],
    queryFn: () => fetchStocktakes({ search, status: statusFilter, page }),
  });

  const items: Stocktake[] = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (!isManagerOrOwner) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
        <ShieldAlert className="w-12 h-12 text-red-500" />
        <h2 className="text-base font-bold text-brand-black">Access Restricted</h2>
        <p className="text-sm text-brand-muted max-w-xs">
          Stocktake management is available to Owner and Manager roles only.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 text-xs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <ClipboardList className="w-5 h-5 text-brand-green" />
            <span>Stocktake Management</span>
          </h1>
          <p className="text-brand-muted mt-0.5">
            Create and manage inventory stock counts
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>New Stocktake</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by title or number..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="w-full pl-8 pr-3 py-2 border border-brand-border rounded-md bg-white text-xs focus:ring-2 focus:ring-brand-green"
          />
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-brand-muted" />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-3 py-2 border border-brand-border rounded-md bg-white text-xs font-semibold"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Error */}
      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Table */}
      <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
        <div className="p-3 bg-brand-bg border-b font-bold text-brand-black flex items-center justify-between">
          <span>Stocktakes ({totalCount})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Number</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Item Type</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Created By</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <Loader2 className="w-6 h-6 text-brand-green animate-spin mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-brand-muted italic">
                    No stocktakes found. Start your first one.
                  </td>
                </tr>
              ) : (
                items.map((s) => {
                  const creator = (s as any).creator?.full_name ?? '—';
                  const completer = (s as any).completer?.full_name;
                  return (
                    <tr
                      key={s.id}
                      className="hover:bg-brand-bg/60 cursor-pointer"
                      onClick={() => setSelectedId(s.id)}
                    >
                      <td className="px-4 py-3 font-mono font-bold text-brand-black">
                        #{s.stocktake_number}
                      </td>
                      <td className="px-4 py-3 font-semibold text-brand-black">{s.title}</td>
                      <td className="px-4 py-3 text-brand-muted capitalize">
                        {s.item_type === 'all' ? 'All Items' : s.item_type}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${
                            s.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : s.status === 'counting'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {s.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-brand-muted">
                        {creator}
                        {completer && <span className="ml-1 text-green-700">(✓ {completer})</span>}
                      </td>
                      <td className="px-4 py-3 text-brand-muted">{formatDate(s.created_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSelectedId(s.id); }}
                          className="inline-flex items-center space-x-1 px-2 py-1 bg-brand-green text-white font-semibold rounded text-[10px] hover:bg-brand-green-hover"
                        >
                          {s.status === 'completed'
                            ? <><ClipboardCheck className="w-3 h-3" /><span>View</span></>
                            : <><ClipboardList className="w-3 h-3" /><span>Count</span></>
                          }
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-brand-border bg-brand-bg">
            <span className="text-brand-muted">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 border rounded font-semibold disabled:opacity-40 hover:bg-white"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 border rounded font-semibold disabled:opacity-40 hover:bg-white"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateStocktakeModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
      {selectedId && (
        <StocktakeCountModal
          stocktakeId={selectedId}
          isOpen={!!selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
};
